/**
 * Uploads an image to R2 Cloudflare storage via the backend API
 * @param {File} file - The image file to upload
 * @param {string} folder - The folder path within the bucket (e.g., 'services', 'portfolio')
 * @param {string} fileName - Optional custom filename (will generate unique if not provided)
 * @returns {Promise<Object>} Upload result with path and public URL
 */
export const uploadImageToStorage = async (file, folder = 'general', fileName = null) => {
  try {
    // Validate the image first
    validateImage(file)
    
    // Generate filename if not provided
    if (!fileName) {
      const timestamp = Date.now()
      const randomString = Math.random().toString(36).substring(7)
      const extension = file.name.split('.').pop()
      fileName = `${timestamp}-${randomString}.${extension}`
    }
    
    // Create FormData for upload
    const formData = new FormData()
    formData.append('file', file)
    formData.append('folder', folder)
    formData.append('fileName', fileName)
    
    // Get API URL - use correct default port
    const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:5000'
    
    // Get auth token from localStorage
    const token = localStorage.getItem('token') || localStorage.getItem('access_token') || ''
    
    console.log('Uploading to R2 via:', `${apiUrl}/api/upload/image`)
    console.log('Using folder:', folder)
    console.log('Using filename:', fileName)
    
    // Upload to backend API which handles R2
    const response = await fetch(`${apiUrl}/api/upload/image`, {
      method: 'POST',
      body: formData,
      headers: {
        // Don't set Content-Type, let browser set it with boundary
        'Authorization': `Bearer ${token}`
      }
    })
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ message: 'Upload failed' }))
      throw new Error(`Upload failed: ${errorData.message || response.statusText}`)
    }
    
    const result = await response.json()
    
    console.log('R2 upload successful:', result)
    
    return {
      path: result.path || `${folder}/${fileName}`,
      publicUrl: result.url || result.publicUrl,
      fileName: fileName
    }
  } catch (error) {
    console.error('Image upload error:', error)
    throw error
  }
}

/**
 * Deletes an image from R2 storage via the backend API
 * @param {string} filePath - The file path in storage
 * @returns {Promise<boolean>} Success status
 */
export const deleteImageFromStorage = async (filePath) => {
  try {
    // Get API URL - use correct default port
    const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:5000'
    
    // Get auth token from localStorage
    const token = localStorage.getItem('token') || localStorage.getItem('access_token') || ''
    
    console.log('Deleting from R2 via:', `${apiUrl}/api/upload/image`)
    console.log('Deleting file path:', filePath)
    
    const response = await fetch(`${apiUrl}/api/upload/image`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify({ filePath })
    })
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ message: 'Delete failed' }))
      throw new Error(`Delete failed: ${errorData.message || response.statusText}`)
    }
    
    console.log('R2 delete successful for:', filePath)
    return true
  } catch (error) {
    console.error('Image delete error:', error)
    throw error
  }
}

/**
 * Gets the public URL for an R2 storage file
 * @param {string} filePath - The file path in storage
 * @returns {string} The public URL
 */
export const getStorageUrl = (filePath) => {
  if (!filePath) return ''
  
  // Get R2 public URL from environment variables
  const r2PublicUrl = import.meta.env.VITE_R2_PUBLIC_URL || 'https://pub-5d1a7afaa6c74e6985f5d6263b64da87.r2.dev'
  
  // Clean up the file path
  const cleanPath = filePath.replace(/^\/+/, '')
  
  return `${r2PublicUrl}/${cleanPath}`
}

/**
 * Extracts the storage path from a full R2 URL
 * @param {string} url - The full R2 storage URL
 * @returns {string} The storage path
 */
export const extractStoragePath = (url) => {
  if (!url) return ''
  
  // Extract path from R2 URL
  const r2PublicUrl = import.meta.env.VITE_R2_PUBLIC_URL || 'https://pub-5d1a7afaa6c74e6985f5d6263b64da87.r2.dev'
  
  if (url.startsWith(r2PublicUrl)) {
    return url.replace(`${r2PublicUrl}/`, '')
  }
  
  // Try to extract from common R2 URL patterns
  const r2UrlPattern = /https:\/\/[^\/]+\.r2\.dev\/(.+)$/
  const match = url.match(r2UrlPattern)
  return match ? match[1] : url
}

/**
 * Formats an image URL by ensuring it has the correct base URL prefix
 * @param {string} imageUrl - The image URL to format
 * @returns {string} The formatted image URL
 */
export const getImageUrl = (imageUrl) => {
  if (!imageUrl) return ''
  
  // If it's already an absolute URL, return as is
  if (imageUrl.startsWith('http://') || imageUrl.startsWith('https://')) {
    return imageUrl
  }
  
  // Clean up the path (remove any leading slashes)
  const cleanPath = imageUrl.replace(/^\/+/, '')
  
  // If it looks like a storage path, get the public URL from R2
  if (cleanPath.includes('/') || cleanPath.includes('services') || cleanPath.includes('portfolio')) {
    return getStorageUrl(cleanPath)
  }
  
  // For relative paths that start with 'images/', treat as storage paths
  if (cleanPath.startsWith('images/')) {
    // Convert images/filename.jpg to general/filename.jpg
    const storagePath = cleanPath.replace('images/', 'general/')
    return getStorageUrl(storagePath)
  }
  
  // Fallback: treat as storage path
  return getStorageUrl(cleanPath)
}

/**
 * Validates an image file
 * @param {File} file - The file to validate
 * @param {number} maxSize - Maximum file size in bytes (default: 10MB)
 * @returns {boolean} True if valid, throws error if invalid
 */
export const validateImage = (file, maxSize = 10 * 1024 * 1024) => {
  // Check file type
  const validTypes = ['image/jpeg', 'image/png', 'image/gif'];
  if (!validTypes.includes(file.type)) {
    throw new Error('Please select a valid image file (JPG, PNG, or GIF)');
  }

  // Check file size
  if (file.size > maxSize) {
    throw new Error(`Image file size must be less than ${maxSize / (1024 * 1024)}MB`);
  }

  return true;
};

/**
 * Creates an object URL for image preview
 * @param {File} file - The image file
 * @returns {string} The object URL for the image
 */
export const createImagePreview = (file) => {
  return URL.createObjectURL(file);
};

/**
 * Revokes an object URL to free up memory
 * @param {string} objectUrl - The object URL to revoke
 */
export const revokeImagePreview = (objectUrl) => {
  if (objectUrl?.startsWith('blob:')) {
    URL.revokeObjectURL(objectUrl);
  }
}; 